import { alpha, Box, Stack, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { getAppBuilderCurrentStyleValue } from 'app/app/editor/tabs/settings/style-editor/StylesEditorContainer';
import { useBlendColors } from 'hooks/useDesign';
import { useAppBuilderContext } from 'app/app/editor/context';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';

interface IndentPreviewProps {
    formState?: any;
    onChangeStyle: (value: any) => void;
    uid: string;
}

interface IndentContainerProps {
    formState: any;
    onChangeStyle: (value: any) => void;
    children?: any;
    type: 'margin' | 'padding';
    uid: string;
}
const IndentContainer = (props: IndentContainerProps) => {
    const appTheme = useTheme();
    const ctx = useAppBuilderContext();
    const node = props.uid == 'page' ? ctx.page.layout : getNodeByUid(ctx.page.layout, props.uid ?? '');
    const layoutSettings = node ? (node?.params?.layout ?? {}) : {};

    const topValRef = useRef<HTMLInputElement>();
    const rightValRef = useRef<HTMLInputElement>();
    const bottomValRef = useRef<HTMLInputElement>();
    const leftValRef = useRef<HTMLInputElement>();
    const focusDirectionRef = useRef<string | null>(null);

    const typeSettings = {
        margin: {
            label: 'Margin',
            bgColor: useBlendColors(alpha(appTheme.palette.primary.main, 0.08), appTheme.palette.background.default),
        },
        padding: {
            label: 'Padding',
            bgColor: useBlendColors(alpha(appTheme.palette.warningLow.main, 0.08), appTheme.palette.background.default),
        },
    };

    const changeValue = (e: any) => {
        props.onChangeStyle({
            [props.type]: [
                topValRef?.current?.textContent ?? '0',
                rightValRef?.current?.textContent ?? '0',
                bottomValRef?.current?.textContent ?? '0',
                leftValRef?.current?.textContent ?? '0',
            ].join(' '),
        });
    };
    const onFocus = (e: any) => {
        focusDirectionRef.current = e.target.dataset.direction;
    };
    const onBlur = () => {
        focusDirectionRef.current = null;
    };
    const updatePreviewValues = () => {
        const currentValue = getAppBuilderCurrentStyleValue(layoutSettings, props.type);
        const parts = currentValue.split(' ').map((item: any) => {
            return item > '' ? item : 0;
        });
        if (parts.length == 4) {
            if (topValRef.current && rightValRef.current && bottomValRef.current && leftValRef.current) {
                topValRef.current.textContent = parts[0];
                rightValRef.current.textContent = parts[1];
                bottomValRef.current.textContent = parts[2];
                leftValRef.current.textContent = parts[3];
            }
        } else {
            if (topValRef.current && rightValRef.current && bottomValRef.current && leftValRef.current) {
                topValRef.current.textContent = parts[0];
                rightValRef.current.textContent = parts[0];
                bottomValRef.current.textContent = parts[0];
                leftValRef.current.textContent = parts[0];
            }
        }
    };

    useEffect(() => {
        updatePreviewValues();
        const sides = [
            { element: topValRef, direction: 'top' },
            { element: rightValRef, direction: 'right' },
            { element: bottomValRef, direction: 'bottom' },
            { element: leftValRef, direction: 'left' },
        ];
        sides.forEach((side) => {
            if (side.element?.current) {
                side.element.current.addEventListener('input', changeValue);
                side.element.current.addEventListener('focus', onFocus);
                side.element.current.addEventListener('blur', onBlur);
            }
        });

        return function () {
            sides.forEach((side) => {
                if (side.element?.current) {
                    side.element.current.removeEventListener('input', changeValue);
                    side.element.current.removeEventListener('focus', onFocus);
                    side.element.current.removeEventListener('blur', onBlur);
                }
            });
        };
    }, []);

    useEffect(() => {
        if (focusDirectionRef.current === null) updatePreviewValues();
    }, [layoutSettings?.[props.type]]);

    return (
        <Stack
            sx={{
                backgroundColor: typeSettings?.[props.type]?.bgColor ?? undefined,
                position: 'relative',
            }}
        >
            <Box sx={{ position: 'absolute', left: '4px', top: '2px' }}>{typeSettings?.[props.type]?.label ?? ''}</Box>
            <Box
                sx={{ textAlign: 'center', p: '2px', '&:focus': { outlineWidth: 0 } }}
                contentEditable={'plaintext-only'}
                suppressContentEditableWarning={true}
                ref={topValRef}
                data-direction="top"
            />
            <Stack direction={'row'} alignItems={'center'}>
                <Box
                    sx={{ flexShrink: 0, p: '4px', '&:focus': { outlineWidth: 0 } }}
                    contentEditable={'plaintext-only'}
                    suppressContentEditableWarning={true}
                    ref={leftValRef}
                    data-direction="left"
                />
                <Box sx={{ flexGrow: 1 }}>{props.children}</Box>
                <Box
                    sx={{ flexShrink: 0, p: '4px', '&:focus': { outlineWidth: 0 } }}
                    contentEditable={'plaintext-only'}
                    suppressContentEditableWarning={true}
                    ref={rightValRef}
                    data-direction="right"
                />
            </Stack>

            <Box
                sx={{ textAlign: 'center', p: '2px', '&:focus': { outlineWidth: 0 } }}
                contentEditable={'plaintext-only'}
                suppressContentEditableWarning={true}
                ref={bottomValRef}
                data-direction="bottom"
            />
        </Stack>
    );
};

export default function IndentPreview({ formState, onChangeStyle = () => {}, uid }: IndentPreviewProps) {
    return (
        <IndentContainer type={'margin'} formState={formState} uid={uid} onChangeStyle={onChangeStyle}>
            <IndentContainer type={'padding'} formState={formState} uid={uid} onChangeStyle={onChangeStyle}>
                <Box
                    sx={{
                        border: (theme) =>
                            `1px solid ${useBlendColors(alpha(theme.palette.text.primary, 0.24), theme.palette.background.default)}`,
                        backgroundColor: (theme) =>
                            useBlendColors(alpha(theme.palette.text.primary, 0.08), theme.palette.background.default),
                        textAlign: 'center',
                    }}
                >
                    Content
                </Box>
            </IndentContainer>
        </IndentContainer>
    );
}
